import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { Gift, Loader2 } from "lucide-react";
import { useRedeem } from "@/hooks/useRewards";
import { REWARDS } from "@/constants/testIds";

/**
 * Redeem points → counter code card. Iter-442+ — redemption unlocks at
 * `minRedeem` pts (API-driven, default 50) and spends in steps of the
 * same size (backend also validates).
 */
export default function RedeemCard({ storeId, balance = 0, minRedeem = 50, prefill = 0 }) {
  const [points, setPoints] = useState("");
  const [lastCode, setLastCode] = useState("");
  const mut = useRedeem();

  // Iter-600 §4 — /rewards?redeem=N (coin-expiry notifications): pre-fill
  // the largest redeemable step ≤ N within balance and bring the panel
  // into view. Fires once, after the balance has loaded.
  const rootRef = useRef(null);
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!prefill || prefilledRef.current || !minRedeem || balance < minRedeem) return;
    const cap = Math.min(Number(prefill) || 0, balance);
    const n = Math.floor(cap / minRedeem) * minRedeem;
    if (n >= minRedeem) {
      prefilledRef.current = true;
      setPoints(String(n));
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [prefill, balance, minRedeem]);

  const disabled = !storeId;
  const insufficient = balance < minRedeem;

  const submit = async (e) => {
    e.preventDefault();
    if (!storeId) {
      toast.error("Pick a store first.");
      return;
    }
    const n = Number(points);
    if (!n || n < minRedeem) {
      toast.error(`Minimum ${minRedeem} points.`);
      return;
    }
    if (n > balance) {
      toast.error("You don't have that many points yet.");
      return;
    }
    if (n % minRedeem !== 0) {
      toast.error(`Redeem in steps of ${minRedeem} points.`);
      return;
    }
    try {
      const res = await mut.mutateAsync({ store_id: storeId, points: n });
      const code = res?.code || res?.counter_code;
      if (code) {
        setLastCode(code);
        confetti({
          particleCount: 80,
          spread: 60,
          origin: { y: 0.4 },
          colors: ["#1CA9E0", "#FFB020", "#22C55E"],
        });
        toast.success(`Redeemed! Show code ${code} at the counter.`);
      } else {
        toast.success("Redeemed.");
      }
      setPoints("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't redeem");
    }
  };

  return (
    <form
      ref={rootRef}
      onSubmit={submit}
      data-testid={REWARDS.redeemCard}
      className="rounded-2xl bg-white shadow-card p-5 md:p-6"
    >
      <div className="flex items-center gap-2 text-sm font-display font-bold text-slate-900 mb-1">
        <Gift className="w-4 h-4 text-[color:var(--brand-orange)]" /> Redeem points
      </div>
      <p className="text-xs text-slate-500 mb-3">
        100 pts = $1 off · Unlocks at {minRedeem} pts · Redeem in steps of {minRedeem}.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 items-stretch">
        <input
          data-testid={REWARDS.redeemInput}
          value={points}
          onChange={(e) => setPoints(e.target.value.replace(/[^0-9]/g, ""))}
          className="input flex-1"
          placeholder={String(minRedeem)}
          disabled={disabled || insufficient}
        />
        <button
          type="submit"
          data-testid={REWARDS.redeemBtn}
          disabled={mut.isPending || disabled || insufficient}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5 disabled:opacity-60 transition-colors"
        >
          {mut.isPending && <Loader2 className="w-4 h-4 animate-spin" />} Redeem
        </button>
      </div>

      {insufficient && (
        <p className="mt-2 text-[11px] text-slate-500">
          You need at least {minRedeem} points — keep collecting!
        </p>
      )}

      {lastCode && (
        <div className="mt-4 rounded-xl bg-[color:var(--brand-blue-wash)] border border-[color:var(--brand-blue)] px-4 py-3 text-center">
          <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[color:var(--brand-blue)]">
            Show at counter
          </div>
          <div className="font-mono font-black text-2xl tracking-widest text-slate-900 mt-1">
            {lastCode}
          </div>
        </div>
      )}
    </form>
  );
}
